import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import api from '@utils/api';
import GalleryGrid from './GalleryGrid';
import MemorySkeleton from './MemorySkeleton';
import PhotoViewer from './PhotoViewer';
import { formatActivityDate } from '@utils/helpers';

const AlbumView = ({ album, onBack }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/memories/activities/${album.activityId}/photos`);
        if (!cancelled) {
          setPhotos(data.photos || []);
          setActivity(data.activity || null);
        }
      } catch {
        if (!cancelled) setPhotos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [album.activityId]);

  const title = `${album.emoji || activity?.emoji || ''} ${album.title || activity?.title || 'Album'}`.trim();

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 24 }}
      transition={{ duration: 0.28 }}
      className="space-y-4"
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--memory-accent)]"
      >
        <ArrowLeft size={16} />
        Back to albums
      </button>

      <div className="memory-card !p-4">
        <h4 className="text-base font-bold text-[var(--memory-text)]">{title}</h4>
        <p className="mt-1 text-xs text-[var(--memory-muted)]">
          {activity?.dateTime || album.dateTime
            ? formatActivityDate(activity?.dateTime || album.dateTime)
            : ''}
          {activity?.location?.address || album.location
            ? ` · ${activity?.location?.address || album.location}`
            : ''}
        </p>
        <p className="mt-1 text-xs text-[var(--memory-muted)]">
          {photos.length || album.photoCount || 0} photos
          {activity?.participantCount != null
            ? ` · ${activity.participantCount} participants`
            : album.participantCount != null
              ? ` · ${album.participantCount} participants`
              : ''}
        </p>
      </div>

      {loading ? (
        <MemorySkeleton />
      ) : (
        <GalleryGrid
          photos={photos}
          onPhotoClick={(p) => {
            const i = photos.findIndex((x) => x._id === p._id);
            setViewerIndex(i >= 0 ? i : 0);
          }}
        />
      )}

      {viewerIndex != null && (
        <PhotoViewer
          photos={photos}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onIndexChange={setViewerIndex}
          onDeleted={(id) => {
            setPhotos((prev) => prev.filter((p) => p._id !== id));
            setViewerIndex(null);
          }}
        />
      )}
    </motion.div>
  );
};

export default AlbumView;
